import { ReactElement } from 'react';
import {
  TextField,
  RichTextField,
  ImageField,
  RichText,
  Text,
  Image,
  Link as NextJSSLink,
  LinkField,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';

import { Link as ReactJSSLink } from '@sitecore-jss/sitecore-jss-react';

type Options = {
  /** If required is `true`, the element will always render. */
  required?: boolean;
  editable?: boolean;
  editModeClassName?: string;
};

type RenderFunction<T> = (field: T | undefined, options?: Options) => ReactElement | undefined;

/** A hook that exposes functions to render JSS Elements conditionally based on:
 * - if the {required} option is set to true
 * - if the page is in edit mode
 * - if the field value is populated
 */
export function useConditionalRendering() {
  const { sitecoreContext } = useSitecoreContext();
  const isEditing = sitecoreContext?.pageEditing ?? false;

  const shouldRender = (value?: string | number, options?: Options) => {
    const { required = false } = options ?? {};
    return required || isEditing || (!isEditing && !!value);
  };

  const fieldProps = (options?: Options) => {
    const { editable = true, editModeClassName } = options ?? {};
    const className = isEditing ? editModeClassName : undefined;
    return { editable, className };
  };

  const renderText: RenderFunction<TextField> = (field, options) =>
    shouldRender(field?.value, options) ? (
      <Text field={field} {...fieldProps(options)} />
    ) : undefined;

  const renderRichText: RenderFunction<RichTextField> = (field, options) =>
    shouldRender(field?.value, options) ? (
      <RichText
        prefetchLinks={process.env.NEXT_PUBLIC_DISABLE_NEXTJS_PREFETCH !== 'true'}
        field={field}
        {...fieldProps(options)}
      />
    ) : undefined;

  const renderLink: RenderFunction<LinkField> = (field, options) => {
    if (!shouldRender(field?.value?.href, options)) {
      return undefined;
    }

    const isMediaLink =
      !isEditing &&
      (field?.value?.linktype === 'media' || field?.value?.href?.includes('/-/media'));

    if (isMediaLink) {
      return (
        <a title={field?.value?.title} target={field?.value?.target} href={field?.value?.href}>
          {field?.value?.text}
        </a>
      );
    }

    /** Returns true for cases where a hard-navigation is desired over a client-side navigation. */
    if (shouldRender(field?.value?.href, options)) {
      return <ReactJSSLink field={field as LinkField} {...fieldProps(options)} />;
    }

    return <NextJSSLink field={field as LinkField} {...fieldProps(options)} />;
  };

  const renderImage: RenderFunction<ImageField> = (field, options) =>
    shouldRender(field?.value?.src, options) ? (
      <Image field={field} {...fieldProps(options)} />
    ) : undefined;

  return {
    shouldRender,
    renderText,
    renderRichText,
    renderLink,
    renderImage,
  };
}
